import { mergeJobAuditsIntoPreview } from "./enrichmentReviewAudit";
import {
  effectiveCatalogSurfaceType,
  effectiveEndpointTypesForValidation,
  isEnrichmentAnchorEntity,
  normName,
  normalizeTypeForCompare,
  resolutionKey,
  rosterTypeOverrideKeyForGap,
} from "./enrichmentEntityRoster";
import { augmentRelMissingEndpoint } from "./enrichmentMissingEndpoint";
import { isReviewBackgroundBusy } from "./enrichmentReviewBackground";

/** Human-readable catalog entity type (e.g. technology-component → technology component). */
export function formatCatalogEntityTypeLabel(entityType) {
  const t = String(entityType || "").trim();
  if (!t) return "entity";
  return t.replace(/-/g, " ");
}

/** True when a label looks like multiple entities joined with /, |, or comma lists. */
export function isCompoundEntityName(name) {
  const text = String(name || "").trim();
  if (!text) return false;
  // Spaced separators only — keep S/4HANA, CI/CD, TCP/IP as one name.
  if (/\s+[/|]\s+/.test(text)) {
    const parts = text
      .split(/\s+[/|]\s+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2 && parts.every((p) => p.length >= 2)) return true;
  }
  if (text.includes(",")) {
    const parts = text
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2 && parts.every((p) => p.length >= 3)) {
      const last = parts[parts.length - 1].toLowerCase();
      if (!/^(inc\.?|llc\.?|ltd\.?|corp\.?|co\.?|plc\.?)$/.test(last)) return true;
    }
  }
  return false;
}

export function compoundEntityNameWarning(name) {
  if (!isCompoundEntityName(name)) return "";
  const label = String(name || "").trim();
  return `"${label}" looks like multiple entities combined. Rename or split it before approving — otherwise they will be modeled as one entity.`;
}

/** Review queue tier: parents before dependents (lower = earlier). */
const CATALOG_REVIEW_QUEUE_PRIORITY = {
  interface_from_flow: 40,
  technology_unassigned: 70,
  implementation_unassigned: 70,
};

/** Retired from enrichment review — apps, interfaces, and tech only. */
const SC_RETIRED_CATALOG_KINDS = new Set([
  "solution_component",
  "interface_internal_sc",
  "solution_technology_link",
  "solution_implementation_link",
]);

function reviewQueueItemPriority(item) {
  if (!item) return 50;
  if (item.type === "name_match") return 0;
  if (item.type === "description") return 5;
  if (item.type === "relationship") return 35;
  if (item.type === "catalog") {
    const kind = String(item.row?.kind || "").trim();
    return CATALOG_REVIEW_QUEUE_PRIORITY[kind] ?? 45;
  }
  return 50;
}

function linkedResolutionForSurface(preview, surfaceName) {
  const key = normName(surfaceName);
  if (!key) return null;
  const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];
  for (const row of rows) {
    if (normName(row?.surface_name) !== key) continue;
    if (String(row?.kind || "") !== "linked_existing") continue;
    const id = String(row?.entity_id || "").trim();
    if (!id) continue;
    return {
      id,
      entityType: String(row?.entity_type || "").trim(),
    };
  }
  return null;
}

/** Wire technology/platform peer ids on catalog rows after a name match or resolution link. */
export function wireCatalogPeerFromEntityMatch(row, surfaceName, entityId, entityType) {
  if (!row || typeof row !== "object") return row;
  const missName = normName(surfaceName);
  const id = String(entityId || "").trim();
  if (!missName || !id) return row;

  const matchesPeer =
    normName(row.technology_component_name) === missName ||
    normName(row.implementation_peer_name) === missName ||
    normName(row.platform_name) === missName;
  if (!matchesPeer) return row;
  if (String(row.implementation_peer_entity_id || "").trim()) return row;

  const out = { ...row };
  out.implementation_peer_entity_id = id;
  out.implementation_peer_candidate_matches = [];

  const actualType = normName(entityType);
  const rowPeerType = normName(
    out.implementation_peer_type || (out.platform_name ? "platform" : "technology-component")
  );
  const wireAsPlatform =
    actualType === "platform" ||
    (actualType !== "technology-component" && rowPeerType === "platform");
  if (wireAsPlatform) {
    out.platform_entity_id = id;
    out.platform_candidate_matches = [];
  } else {
    out.technology_component_entity_id = id;
    out.technology_candidate_matches = [];
  }
  return out;
}

function inferCatalogAppEndpointsFromResolution(row, preview) {
  if (!row || typeof row !== "object") return row;
  let next = row;
  const provName = String(next.provider_application_name || "").trim();
  const consName = String(next.consumer_application_name || "").trim();
  if (provName && !String(next.provider_entity_id || "").trim()) {
    const linked = linkedResolutionForSurface(preview, provName);
    if (linked?.id) {
      next = {
        ...next,
        provider_entity_id: linked.id,
        provider_candidate_matches: [],
      };
    }
  }
  if (consName && !String(next.consumer_entity_id || "").trim()) {
    const linked = linkedResolutionForSurface(preview, consName);
    if (linked?.id) {
      next = {
        ...next,
        consumer_entity_id: linked.id,
        consumer_candidate_matches: [],
      };
    }
  }
  return next;
}

/** Re-wire interface catalog rows after resolution links; clears stale quarantine. */
export function refreshSolutionStructureCatalogRows(preview) {
  if (!preview) return preview;
  const stubs = (Array.isArray(preview.proposed_catalog_entities)
    ? preview.proposed_catalog_entities
    : []
  ).map((row) => {
    if (!row || typeof row !== "object") return row;
    const next = inferCatalogAppEndpointsFromResolution(row, preview);
    const blocked = catalogBlockedMessageForRow(next, preview);
    return { ...next, quarantine_reason: blocked || null };
  });
  return { ...preview, proposed_catalog_entities: stubs };
}

export function catalogStubKey(row, idx) {
  return row?.interface_suggestion_key || `stub-${idx}`;
}

/** @returns {"approved"|"discarded"|null} */
export function relationshipReviewDecision(preview, rel, idx, relActions = {}) {
  if (String(rel?.existing_relationship_id || "").trim()) return null;
  const key = `p${idx}`;
  const live = relActions[key]?.state;
  const po = preview?.enrichment_review_outcomes?.proposed_relationships?.[key];
  if (live === "approved" || po === "approved") return "approved";
  if (live === "discarded" || po === "discarded") return "discarded";
  return null;
}

/** @returns {"approved"|"discarded"|null} */
export function catalogReviewDecision(preview, row, idx, catalogActions = {}) {
  const ck = catalogStubKey(row, idx);
  const live = catalogActions[ck]?.state;
  const po = preview?.enrichment_review_outcomes?.catalog_entities?.[ck];
  if (live === "created" || po === "created") return "approved";
  if (live === "discarded" || po === "discarded") return "discarded";
  return null;
}

/** True while Add-to-map is in flight for this catalog stub (steward already decided). */
export function catalogReviewInFlight(preview, row, idx, catalogActions = {}) {
  const ck = catalogStubKey(row, idx);
  const live = catalogActions[ck]?.state;
  const po = preview?.enrichment_review_outcomes?.catalog_entities?.[ck];
  return live === "creating" || po === "creating";
}

/** True while a relationship approve/discard save is in flight. */
export function relationshipReviewInFlight(preview, rel, idx, relActions = {}) {
  const key = `p${idx}`;
  const live = relActions[key]?.state;
  const po = preview?.enrichment_review_outcomes?.proposed_relationships?.[key];
  return (
    live === "approving" ||
    live === "discarding" ||
    po === "approving" ||
    po === "discarding"
  );
}

/** Any catalog/relationship outcomes still mid-save on the preview. */
export function previewHasInFlightReviewSaves(preview) {
  if (!preview) return false;
  const ce = preview?.enrichment_review_outcomes?.catalog_entities || {};
  if (Object.values(ce).some((v) => v === "creating")) return true;
  const pr = preview?.enrichment_review_outcomes?.proposed_relationships || {};
  if (Object.values(pr).some((v) => v === "approving" || v === "discarding")) {
    return true;
  }
  return false;
}

export function countInFlightReviewSaves(preview) {
  if (!preview) return 0;
  let n = 0;
  const ce = preview?.enrichment_review_outcomes?.catalog_entities || {};
  Object.values(ce).forEach((v) => {
    if (v === "creating") n += 1;
  });
  const pr = preview?.enrichment_review_outcomes?.proposed_relationships || {};
  Object.values(pr).forEach((v) => {
    if (v === "approving" || v === "discarding") n += 1;
  });
  return n;
}

/**
 * Auto-complete when the steward has no remaining cards and every attended
 * create/save has finished (including background saves after leave).
 */
export function shouldAutoCompleteEnrichmentReview(
  preview,
  stewardPendingCount,
  { backgroundBusy = false } = {}
) {
  if (!preview) return false;
  if (preview?.enrichment_review_outcomes?.review_complete) return false;
  if (Number(stewardPendingCount) > 0) return false;
  if (backgroundBusy) return false;
  if (previewHasInFlightReviewSaves(preview)) return false;
  return true;
}

function relRowComplete(preview, rel, idx, relActions) {
  if (String(rel?.existing_relationship_id || "").trim()) return true;
  return relationshipReviewDecision(preview, rel, idx, relActions) != null;
}

/** UI state for a proposed relationship row (live actions + persisted outcomes). */
export function relationshipUiState(preview, rel, idx, relActions = {}) {
  const key = `p${idx}`;
  const live = relActions[key] || {};
  const po = preview?.enrichment_review_outcomes?.proposed_relationships?.[key];
  // Live terminal outcomes beat stale persisted in-flight flags (e.g. approving after save finished).
  if (live.state === "approved" || live.state === "discarded" || live.state === "error") {
    return {
      state: live.state,
      error: live.error || "",
      relationshipId: live.relationshipId,
    };
  }
  if (po === "approved" || po === "discarded") {
    return {
      state: po,
      error: live.error || "",
      relationshipId: live.relationshipId,
    };
  }
  if (po === "approving" || po === "discarding") {
    return {
      state: po,
      error: live.error || "",
      relationshipId: live.relationshipId,
    };
  }
  if (["approving", "error", "discarding"].includes(live.state)) {
    return live;
  }
  if (live.state === "approved" || live.state === "discarded") {
    return live;
  }
  if (String(rel?.existing_relationship_id || "").trim()) {
    return { state: "approved", error: "", relationshipId: rel.existing_relationship_id };
  }
  return live;
}

/** UI state for a catalog / interface stub row. */
export function catalogUiState(preview, row, idx, catalogActions = {}) {
  const ck = catalogStubKey(row, idx);
  const live = catalogActions[ck] || {};
  if (live.state === "creating" || live.state === "error") return live;
  if (live.state) return live;
  const po = preview?.enrichment_review_outcomes?.catalog_entities?.[ck];
  if (po === "created") return { state: "created", error: "" };
  if (po === "discarded") return { state: "discarded", error: "" };
  if (po === "creating") return { state: "creating", error: live.error || "" };
  return live;
}

export function clearProposedRelationshipOutcome(prev, key) {
  if (!prev || !key) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const pr = { ...(outcomes.proposed_relationships || {}) };
  delete pr[key];
  return {
    ...prev,
    enrichment_review_outcomes: {
      ...outcomes,
      proposed_relationships: pr,
      review_complete: false,
    },
  };
}

export function clearCatalogEntityOutcome(prev, catalogKey) {
  if (!prev || !catalogKey) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const ce = { ...(outcomes.catalog_entities || {}) };
  delete ce[catalogKey];
  return {
    ...prev,
    enrichment_review_outcomes: {
      ...outcomes,
      catalog_entities: ce,
      review_complete: false,
    },
  };
}

export function normalizeDescriptionText(text) {
  return String(text || "")
    .trim()
    .replace(/\s+/g, " ");
}

/** True when the description card should not appear in the active review queue. */
export function isDescriptionReviewResolved(preview, options = {}) {
  if (!preview?.description_proposal?.proposed_description) return true;
  const outcomes = preview?.enrichment_review_outcomes || {};
  if (outcomes.description_applied || outcomes.description_discarded) return true;
  if (options.descAccepted || options.descSkipped) return true;
  return false;
}

export function setDescriptionAppliedOutcome(prev) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const { description_discarded: _d, ...rest } = outcomes;
  return {
    ...prev,
    enrichment_review_outcomes: { ...rest, description_applied: true },
  };
}

export function setDescriptionDiscardedOutcome(prev) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const { description_applied: _a, ...rest } = outcomes;
  return {
    ...prev,
    enrichment_review_outcomes: { ...rest, description_discarded: true },
  };
}

export function clearDescriptionAppliedOutcome(prev) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const {
    description_applied: _a,
    description_discarded: _d,
    ...rest
  } = outcomes;
  return {
    ...prev,
    enrichment_review_outcomes: { ...rest, review_complete: false },
  };
}

export function setReviewCompleteOutcome(prev, complete = true) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  return {
    ...prev,
    enrichment_review_outcomes: { ...outcomes, review_complete: Boolean(complete) },
  };
}

export function displayNameOverrideKey(item) {
  if (!item) return "";
  if (item.type === "relationship") return `p${item.idx}`;
  if (item.type === "catalog") return `cat-${catalogStubKey(item.row, item.idx)}`;
  return item.id || "";
}

export function getDisplayNameOverride(preview, item) {
  const key = displayNameOverrideKey(item);
  if (!key) return "";
  return String(
    preview?.enrichment_review_outcomes?.display_name_overrides?.[key] || ""
  ).trim();
}

export function setDisplayNameOverride(prev, item, name) {
  if (!prev || !item) return prev;
  const key = displayNameOverrideKey(item);
  if (!key) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const map = { ...(outcomes.display_name_overrides || {}) };
  const trimmed = String(name || "").trim();
  if (trimmed) map[key] = trimmed;
  else delete map[key];
  return {
    ...prev,
    enrichment_review_outcomes: { ...outcomes, display_name_overrides: map },
  };
}

function replaceNormMatchedField(obj, field, oldName, newName) {
  if (!obj || typeof obj !== "object") return obj;
  if (normName(obj[field]) !== normName(oldName)) return obj;
  return { ...obj, [field]: newName };
}

function migrateRenameOutcomeKeys(outcomes, oldName, newName, entityType) {
  if (!outcomes || normName(oldName) === normName(newName)) return outcomes;
  const next = { ...outcomes };
  const oldPk = resolutionKey({ surface_name: oldName, entity_type: entityType });
  const newPk = resolutionKey({ surface_name: newName, entity_type: entityType });

  const typeOverrides = { ...(next.entity_type_overrides || {}) };
  for (const prefix of ["missing", "resolution"]) {
    const oldKey = `${prefix}:${oldPk}`;
    const newKey = `${prefix}:${newPk}`;
    if (typeOverrides[oldKey]) {
      typeOverrides[newKey] = typeOverrides[oldKey];
      delete typeOverrides[oldKey];
    }
  }
  next.entity_type_overrides = typeOverrides;

  const resolutionMatches = { ...(next.resolution_matches || {}) };
  if (resolutionMatches[oldPk]) {
    resolutionMatches[newPk] = resolutionMatches[oldPk];
    delete resolutionMatches[oldPk];
  }
  next.resolution_matches = resolutionMatches;
  return next;
}

function propagateSurfaceNameInPreview(prev, oldName, newName, entityType) {
  const oldNorm = normName(oldName);
  const trimmedNew = String(newName || "").trim();
  const typeNorm = normName(entityType);
  if (!prev || !oldNorm || !trimmedNew || normName(trimmedNew) === oldNorm) return prev;

  const relFields = [
    "missing_surface_name",
    "surface_target_name",
    "surface_source_name",
    "surface_other_application",
    "surface_child_application",
  ];
  const catalogFields = [
    "suggested_name",
    "interface_name",
    "technology_component_name",
    "implementation_peer_name",
    "platform_name",
    "provider_application_name",
    "consumer_application_name",
  ];

  const patchFields = (obj, fields) => {
    if (!obj || typeof obj !== "object") return obj;
    let next = obj;
    for (const field of fields) {
      const patched = replaceNormMatchedField(next, field, oldName, trimmedNew);
      if (patched !== next) next = patched;
    }
    return next;
  };

  const next = {
    ...prev,
    resolution: (prev.resolution || []).map((row) => {
      if (normName(row?.surface_name) !== oldNorm) return row;
      if (typeNorm && normName(row?.entity_type) !== typeNorm) return row;
      return { ...row, surface_name: trimmedNew };
    }),
    proposed_relationships: (prev.proposed_relationships || []).map((rel) =>
      patchFields(rel, relFields)
    ),
    proposed_catalog_entities: (prev.proposed_catalog_entities || []).map((row) =>
      patchFields(row, catalogFields)
    ),
    enrichment_review_outcomes: migrateRenameOutcomeKeys(
      prev.enrichment_review_outcomes,
      oldName,
      trimmedNew,
      entityType
    ),
  };
  return refreshSolutionStructureCatalogRows(next);
}

function relationshipEditableEntities(rel) {
  const r = augmentRelMissingEndpoint(rel);
  const missName = String(r?.missing_surface_name || "").trim();
  const missType = String(r?.missing_entity_type || "").trim();
  if (!missName || !missType) return [];
  const end = String(r?.missing_end || "target").trim();
  const endId = end === "source" ? r?.source_entity_id : r?.target_entity_id;
  if (String(endId || "").trim()) return [];
  return [
    {
      key: "missing_peer",
      label: formatCatalogEntityTypeLabel(missType),
      name: missName,
      entityType: missType,
    },
  ];
}

function catalogEditableEntities(row, preview = null, reviewEntity = null) {
  if (!row || typeof row !== "object") return [];
  const out = [];
  const ends = interfaceCatalogEndpoints(row, preview, reviewEntity);
  if (ends) {
    if (ends.source) {
      out.push({
        key: "provider",
        label: "Source",
        name: ends.source.name,
        entityType: ends.source.type,
      });
    }
    if (ends.target) {
      out.push({
        key: "consumer",
        label: "Target",
        name: ends.target.name,
        entityType: ends.target.type,
      });
    }
    const ifaceName = String(row.suggested_name || row.interface_name || "").trim();
    if (ifaceName && !String(row.existing_interface_id || "").trim()) {
      out.push({
        key: "interface",
        label: "Interface",
        name: ifaceName,
        entityType: "interface",
      });
    }
    return out;
  }

  return out;
}

/** Entities on a review card whose extracted names stewards may correct before approve. */
export function reviewItemEditableEntities(item, preview = null, reviewEntity = null) {
  if (!item) return [];
  if (item.type === "name_match") {
    const row = item.row || {};
    const name = String(row.surface_name || "").trim();
    const entityType = String(row.entity_type || "application").trim();
    if (!name) return [];
    return [
      {
        key: "resolution",
        label: formatCatalogEntityTypeLabel(entityType),
        name,
        entityType,
      },
    ];
  }
  if (item.type === "relationship") {
    return relationshipEditableEntities(item.row);
  }
  if (item.type === "catalog") {
    return catalogEditableEntities(item.row, preview, reviewEntity);
  }
  return [];
}

/** Rename an extracted entity on a review card and propagate the new name through the preview. */
export function renameReviewItemEntity(prev, item, entityKey, newName) {
  if (!prev || !item) return prev;
  const trimmed = String(newName || "").trim();
  if (!trimmed) return prev;

  if (item.type === "name_match" && entityKey === "resolution") {
    const row = item.row || {};
    const oldName = String(row.surface_name || "").trim();
    const entityType = String(row.entity_type || "application").trim();
    if (!oldName) return prev;
    return propagateSurfaceNameInPreview(prev, oldName, trimmed, entityType);
  }

  if (item.type === "relationship") {
    const target = relationshipEditableEntities(item.row).find((e) => e.key === entityKey);
    if (!target) return prev;
    const idx = item.idx;
    const proposed = (prev.proposed_relationships || []).map((rel, i) => {
      if (i !== idx) return rel;
      const r = { ...augmentRelMissingEndpoint(rel) };
      if (entityKey !== "missing_peer") return r;
      r.missing_surface_name = trimmed;
      const end = String(r.missing_end || "target").trim();
      if (end === "target" && normName(r.surface_target_name) === normName(target.name)) {
        r.surface_target_name = trimmed;
      }
      if (end === "source" && normName(r.surface_source_name) === normName(target.name)) {
        r.surface_source_name = trimmed;
      }
      return r;
    });
    return propagateSurfaceNameInPreview(
      { ...prev, proposed_relationships: proposed },
      target.name,
      trimmed,
      target.entityType
    );
  }

  if (item.type === "catalog") {
    const target = catalogEditableEntities(item.row, prev, null).find((e) => e.key === entityKey);
    if (!target) return prev;
    const idx = item.idx;
    const proposed = (prev.proposed_catalog_entities || []).map((row, i) => {
      if (i !== idx) return row;
      const copy = { ...row };
      switch (entityKey) {
        case "provider":
          copy.provider_application_name = trimmed;
          break;
        case "consumer":
          copy.consumer_application_name = trimmed;
          break;
        case "interface":
          copy.interface_name = trimmed;
          copy.suggested_name = trimmed;
          break;
        default:
          break;
      }
      return copy;
    });
    return propagateSurfaceNameInPreview(
      { ...prev, proposed_catalog_entities: proposed },
      target.name,
      trimmed,
      target.entityType
    );
  }

  return prev;
}

/** Apply steward rename to a relationship row before catalog create. */
export function applyRelationshipDisplayOverride(rel, preview, idx) {
  if (!rel || typeof rel !== "object") return rel;
  const override = getDisplayNameOverride(preview, { type: "relationship", idx, row: rel });
  if (!override) return rel;
  const next = { ...rel };
  if (String(next.missing_surface_name || "").trim()) {
    next.missing_surface_name = override;
  } else if (String(next.surface_target_name || "").trim()) {
    next.surface_target_name = override;
  } else if (String(next.surface_source_name || "").trim()) {
    next.surface_source_name = override;
  } else {
    next.missing_surface_name = override;
  }
  return next;
}

function catalogRowComplete(preview, row, idx, catalogActions) {
  return catalogReviewDecision(preview, row, idx, catalogActions) != null;
}

/** Drop interrupted in-progress outcomes so approve handlers are not stuck silently.
 * Keeps `creating` / `approving` while a background save session is still in flight
 * for this job (user left the wizard; work continues asynchronously).
 */
export function stripStaleInProgressReviewOutcomes(prev, { jobId = "" } = {}) {
  if (!prev?.enrichment_review_outcomes) return prev;
  const outcomes = { ...prev.enrichment_review_outcomes };
  let changed = false;
  const backgroundBusy = Boolean(jobId && isReviewBackgroundBusy(jobId));

  const pr = { ...(outcomes.proposed_relationships || {}) };
  Object.keys(pr).forEach((key) => {
    if (pr[key] === "approving" || pr[key] === "discarding") {
      if (backgroundBusy && pr[key] === "approving") return;
      delete pr[key];
      changed = true;
    }
  });

  const ce = { ...(outcomes.catalog_entities || {}) };
  Object.keys(ce).forEach((key) => {
    if (ce[key] === "creating") {
      if (backgroundBusy) return;
      delete ce[key];
      changed = true;
    }
  });

  if (!changed) return prev;
  return {
    ...prev,
    enrichment_review_outcomes: {
      ...outcomes,
      proposed_relationships: pr,
      catalog_entities: ce,
    },
  };
}

/**
 * Apply top catalog candidate matches onto interface stub provider/consumer ids.
 */
export function applyCatalogEndpointCandidates(row) {
  if (!row || typeof row !== "object") return row;
  const out = { ...row };
  const firstId = (matches) => String(matches?.[0]?.id || "").trim();
  if (!String(out.provider_entity_id || "").trim()) {
    const pid = firstId(out.provider_candidate_matches);
    if (pid) {
      out.provider_entity_id = pid;
      out.provider_candidate_matches = [];
    }
  }
  if (!String(out.consumer_entity_id || "").trim()) {
    const cid = firstId(out.consumer_candidate_matches);
    if (cid) {
      out.consumer_entity_id = cid;
      out.consumer_candidate_matches = [];
    }
  }
  if (!String(out.technology_component_entity_id || "").trim()) {
    const tid = firstId(out.technology_candidate_matches);
    if (tid) {
      out.technology_component_entity_id = tid;
      out.technology_candidate_matches = [];
    }
  }
  if (!String(out.platform_entity_id || "").trim()) {
    const pid = firstId(out.platform_candidate_matches);
    if (pid) {
      out.platform_entity_id = pid;
      out.platform_candidate_matches = [];
    }
  }
  if (!String(out.implementation_peer_entity_id || "").trim()) {
    const peerType = String(
      out.implementation_peer_type || out.entity_type || "technology-component"
    ).trim();
    const peerId = firstId(
      out.implementation_peer_candidate_matches ||
        (peerType === "platform"
          ? out.platform_candidate_matches
          : out.technology_candidate_matches)
    );
    if (peerId) {
      out.implementation_peer_entity_id = peerId;
      out.implementation_peer_candidate_matches = [];
      if (peerType === "platform") {
        out.platform_entity_id = peerId;
        out.platform_candidate_matches = [];
      } else {
        out.technology_component_entity_id = peerId;
        out.technology_candidate_matches = [];
      }
    }
  }
  return out;
}

/**
 * Restore in-memory review action maps from persisted preview outcomes when reopening a job.
 */
export function hydrateReviewActionsFromPreview(preview) {
  const outcomes = preview?.enrichment_review_outcomes || {};
  const relActions = {};
  const catalogActions = {};
  const matchActions = {};
  const rosterRowActions = {};

  Object.entries(outcomes.proposed_relationships || {}).forEach(([key, status]) => {
    if (status === "approved" || status === "discarded") {
      relActions[key] = { state: status, error: "" };
    } else if (status === "approving" || status === "discarding") {
      relActions[key] = { state: status, error: "" };
    }
  });
  Object.entries(outcomes.catalog_entities || {}).forEach(([ck, status]) => {
    if (status === "created") {
      catalogActions[ck] = { state: "created", error: "" };
    } else if (status === "discarded") {
      catalogActions[ck] = { state: "discarded", error: "" };
    } else if (status === "creating") {
      catalogActions[ck] = { state: "creating", error: "" };
    }
  });
  Object.entries(outcomes.resolution_matches || {}).forEach(([rk, status]) => {
    if (status === "confirmed") {
      matchActions[rk] = { state: "confirmed", error: "" };
    } else if (status === "kept_new") {
      matchActions[rk] = { state: "kept_new", error: "" };
    } else if (status === "discarded") {
      matchActions[rk] = { state: "discarded", error: "" };
    }
  });
  Object.entries(outcomes.entity_roster_approvals || {}).forEach(([rk, status]) => {
    if (status === "approved") {
      rosterRowActions[rk] = { state: "approved", error: "" };
    } else if (status === "discarded") {
      rosterRowActions[rk] = { state: "discarded", error: "" };
    }
  });

  return {
    relActions,
    catalogActions,
    matchActions,
    rosterRowActions,
    descriptionApplied: Boolean(
      outcomes.description_applied || outcomes.description_discarded
    ),
    descriptionDiscarded: Boolean(outcomes.description_discarded),
  };
}

/** Omitted from the one-at-a-time queue (handled via catalog interface cards or auto-marked). */
export const HIDDEN_REVIEW_REL_KINDS = new Set([
  "solution_data_usage",
  "solution_component_parent",
  "solution_uses_technology",
  "solution_runs_on_platform",
  "interface_flow",
  "interface_link",
  "interface_link_internal_sc",
  "component_interface_link",
]);

/** App-level tech/platform hints — no review action needed; default is keep at application level. */
export const HIDDEN_REVIEW_CATALOG_KINDS = new Set([
  "technology_unassigned",
  "implementation_unassigned",
]);

function isHiddenReviewCatalogRow(row) {
  const kind = String(row?.kind || "").trim();
  if (HIDDEN_REVIEW_CATALOG_KINDS.has(kind)) return true;
  return SC_RETIRED_CATALOG_KINDS.has(kind);
}

function relPeerLabel(rel) {
  return (
    rel?.peer_name ||
    rel?.surface_target_name ||
    rel?.surface_source_name ||
    rel?.surface_other_application ||
    rel?.surface_child_application ||
    rel?.missing_surface_name ||
    "—"
  );
}

/** Badge label for review queue relationship cards. */
export function relationshipKindLabel(rel) {
  const source = String(rel?.proposal_source || "").trim().toLowerCase();
  if (source === "catalog_similarity") return "catalog recommendation";
  if (source === "knowledge_extract") return "from linked knowledge";
  const status = String(rel?.status || rel?.diff_status || "").trim().toLowerCase();
  if (status === "obsolete" || String(rel?.kind || "").trim().toLowerCase() === "obsolete") {
    return "obsolete";
  }
  return String(rel?.kind || rel?.relationship_type || "relationship").replace(/_/g, " ");
}

/** True when the proposal came from catalog similarity (not evidence). */
export function isCatalogRecommendationRel(rel) {
  return (
    String(rel?.proposal_source || "").trim().toLowerCase() === "catalog_similarity" ||
    rel?.recommendation === true
  );
}

/** Resolution row that still needs a steward name-matching decision. */
export function isNameMatchReviewRow(row, preview, editingEntity = null) {
  if (!row) return false;
  const kind = String(row.kind || "").trim();
  if (kind === "linked_existing" || kind === "discarded") return false;
  if (!String(row.surface_name || "").trim()) return false;
  if (
    isEnrichmentAnchorEntity(preview, editingEntity, {
      surfaceName: row.surface_name,
      entityType: row.entity_type,
      entityId: row.entity_id || row.linked_entity_id,
    })
  ) {
    return false;
  }
  return true;
}

export function nameMatchHasSuggestions(row) {
  return Array.isArray(row?.candidate_matches) && row.candidate_matches.length > 0;
}

/** @deprecated alias — review rows include names with or without catalog suggestions. */
export function isNameMatchResolutionCandidate(row, preview, editingEntity = null) {
  return isNameMatchReviewRow(row, preview, editingEntity);
}

function nameMatchReviewDecision(preview, row, matchActions = {}) {
  const rk = resolutionKey(row);
  const live = matchActions[rk] || {};
  if (
    live.state === "confirmed" ||
    live.state === "kept_new" ||
    live.state === "discarded"
  ) {
    return live.state;
  }
  const persisted = preview?.enrichment_review_outcomes?.resolution_matches?.[rk];
  if (
    persisted === "confirmed" ||
    persisted === "kept_new" ||
    persisted === "discarded"
  ) {
    return persisted;
  }
  return null;
}

/**
 * True while any name-matching resolution row still needs a steward decision.
 */
export function hasUndecidedNameMatches(preview, editingEntity = null, matchActions = {}) {
  if (!preview) return false;
  const rows = Array.isArray(preview.resolution) ? preview.resolution : [];
  return rows.some(
    (row) =>
      isNameMatchReviewRow(row, preview, editingEntity) &&
      !nameMatchReviewDecision(preview, row, matchActions)
  );
}

/**
 * Steward decision for an extracted surface name from name matching / resolution.
 * @returns {"kept_new"|"confirmed"|"discarded"|null}
 */
export function resolutionSurfaceDecision(preview, surfaceName, entityType = "") {
  const sn = normName(surfaceName);
  if (!sn || !preview) return null;
  const typeWanted = normName(entityType);
  const outcomes = preview?.enrichment_review_outcomes?.resolution_matches || {};
  const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];

  const decideForRow = (row) => {
    const rk = resolutionKey(row);
    const st = outcomes[rk];
    if (st === "kept_new" || st === "confirmed" || st === "discarded") return st;
    const kind = String(row?.kind || "").trim();
    if (kind === "linked_existing" && String(row?.entity_id || "").trim()) {
      return "confirmed";
    }
    if (kind === "create_new" && String(row?.reason_code || "") === "user_kept_as_new") {
      return "kept_new";
    }
    return null;
  };

  for (const row of rows) {
    if (normName(row?.surface_name) !== sn) continue;
    if (typeWanted && normName(row?.entity_type) !== typeWanted) continue;
    const d = decideForRow(row);
    if (d) return d;
  }

  if (typeWanted) {
    for (const row of rows) {
      if (normName(row?.surface_name) !== sn) continue;
      const d = decideForRow(row);
      if (d) return d;
    }
  }
  return null;
}

function formatPendingCreateLabel(typeLabel, name) {
  return `${typeLabel} "${name}" (already marked to add as new)`;
}

function formatStillMissingLabel(typeLabel, name) {
  return `${typeLabel} "${name}"`;
}

/**
 * Classify a missing endpoint relative to name-matching decisions.
 * pending_create = steward already chose Add as new — approve can create it (interfaces/apps).
 * still_missing = no decision yet — truly blocks until match/create.
 * allowPendingCreate: false for solution links that need real entity ids before approve.
 */
function classifyMissingSurface(
  preview,
  name,
  entityType,
  typeLabel,
  { allowPendingCreate = true } = {}
) {
  const surface = String(name || "").trim();
  if (!surface) return null;
  const label = typeLabel || formatCatalogEntityTypeLabel(entityType) || "Entity";
  const decision = resolutionSurfaceDecision(preview, surface, entityType);
  if (decision === "kept_new" && allowPendingCreate) {
    return { status: "pending_create", text: formatPendingCreateLabel(label, surface) };
  }
  if (decision === "kept_new" && !allowPendingCreate) {
    return {
      status: "still_missing",
      text: `${label} "${surface}" (marked to add as new — create or link it first)`,
    };
  }
  // confirmed without an id is handled by wiring; still treat as missing until wired
  return { status: "still_missing", text: formatStillMissingLabel(label, surface) };
}

function composeGapMessages(parts) {
  const pending = parts.filter((p) => p?.status === "pending_create");
  const missing = parts.filter((p) => p?.status === "still_missing");
  if (!pending.length && !missing.length) {
    return { blocked: false, message: "" };
  }
  if (!missing.length) {
    const names = pending.map((p) => p.text).join(" and ");
    return {
      blocked: false,
      message: `${names}. We'll create ${
        pending.length === 1 ? "it" : "them"
      } when you add this to your map.`,
    };
  }
  const missingText = missing.map((p) => p.text).join(" and ");
  if (pending.length) {
    const pendingText = pending.map((p) => p.text).join(" and ");
    return {
      blocked: true,
      message: `${missingText} not on your map yet. ${pendingText}.`,
    };
  }
  return {
    blocked: true,
    message: `${missingText} not on your map yet.`,
  };
}

export function nameMatchGuidanceCopy(surfaceName, entityType, hasSuggestions = true) {
  const name = String(surfaceName || "this name").trim() || "this name";
  if (hasSuggestions) {
    return `We found "${name}" in your upload. It may already be on your map.`;
  }
  return `We found "${name}" in your upload. It isn't on your map yet.`;
}

/** Prompt for a suggested duplicate match on a name-match review card. */
export function nameMatchDuplicatePrompt(surfaceName, candidateLabel) {
  const surface = String(surfaceName || "this name").trim() || "this name";
  const candidate = String(candidateLabel || "an existing entity").trim() || "an existing entity";
  return `Do you think "${surface}" is a duplicate of "${candidate}"? If so, the recommended action is Link to existing.`;
}

/** Subtle endpoint type labels for relationship review cards. */
export function relationshipEndpointTypeParts(anchorName, rel, preview, editingEntity = null) {
  const r = augmentRelMissingEndpoint(rel);
  const anchor = String(anchorName || preview?.anchor_name || "Application").trim();
  const overrides = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
  const { sourceType, targetType } = effectiveEndpointTypesForValidation(
    r,
    overrides,
    preview,
    editingEntity || {}
  );
  if (!sourceType && !targetType) return [];

  const rt = String(r.relationship_type || r.kind || "").trim();
  let sourceName = String(r.surface_source_name || r.surface_other_application || "").trim();
  let targetName = String(
    r.surface_target_name || r.surface_other_application || r.surface_child_application || ""
  ).trim();
  const peer = String(relPeerLabel(r) || "").trim();

  if (!sourceName && !targetName) {
    if (
      rt === "organization uses application" ||
      rt === "initiative affects application" ||
      rt === "application runs on platform"
    ) {
      sourceName = peer || "—";
      targetName = anchor;
    } else {
      sourceName = anchor;
      targetName = peer || "—";
    }
  } else {
    if (!sourceName) sourceName = anchor;
    if (!targetName) targetName = anchor;
  }

  const parts = [];
  if (sourceName && sourceType) {
    parts.push({ name: sourceName, type: sourceType });
  }
  if (
    targetName &&
    targetType &&
    (targetName !== sourceName || targetType !== sourceType)
  ) {
    parts.push({ name: targetName, type: targetType });
  }
  return parts;
}

/** Subtle endpoint type labels for catalog / interface review cards. */
export function catalogEndpointTypeParts(row, preview, reviewEntity = null) {
  if (!row || typeof row !== "object") return [];
  const ends = interfaceCatalogEndpoints(row, preview, reviewEntity);
  if (ends) {
    const parts = [];
    if (ends.source) {
      parts.push({
        name: ends.source.name,
        type: ends.source.type,
        role: "source",
        roleLabel: "Source",
      });
    }
    if (ends.target) {
      parts.push({
        name: ends.target.name,
        type: ends.target.type,
        role: "target",
        roleLabel: "Target",
      });
    }
    return parts;
  }

  const parts = [];
  return parts;
}

function relShortVerb(rel) {
  const rt = String(rel?.relationship_type || rel?.kind || "").trim();
  const map = {
    "application uses technology-component": "uses",
    "application accesses data-object": "accesses",
    "application runs on platform": "runs on",
    "initiative affects application": "related to initiative",
    "organization uses application": "used by",
    "application supports business-capability": "supports",
    "application supports business-context": "supports",
    provides: "provides",
    consumes: "consumes",
    owns: "owns",
  };
  return map[rt] || map[String(rel?.kind || "")] || "linked to";
}

/** Short claim for queue card (wireframe style). */
export function compactRelationshipClaim(anchorName, rel) {
  const anchor = String(anchorName || "This application").trim() || "Application";
  const peer = relPeerLabel(rel);
  const verb = relShortVerb(rel);
  const rt = String(rel?.relationship_type || "").trim();
  if (rt === "organization uses application") {
    return `${anchor} → used by → ${peer}`;
  }
  if (rt === "application uses technology-component") {
    return `${anchor} → uses → ${peer}`;
  }
  if (rt === "application accesses data-object") {
    return `${anchor} → accesses → ${peer}`;
  }
  if (rt === "application runs on platform") {
    return `${peer} → runs → ${anchor}`;
  }
  if (rt === "initiative affects application") {
    return `${peer} → affects → ${anchor}`;
  }
  if (String(rel?.kind || "") === "interface_flow") {
    if (rt === "consumer_from_provider") {
      const src = String(rel?.surface_source_name || peer || "—").trim();
      return `${src} → data flow → ${anchor}`;
    }
    const tgt = String(rel?.surface_target_name || peer || "—").trim();
    return `${anchor} → data flow → ${tgt}`;
  }
  return `${anchor} → ${verb} → ${peer}`;
}

export function compactCatalogClaim(row) {
  const kind = String(row?.kind || "").trim();
  const name = String(row?.suggested_name || row?.interface_name || "Interface").trim();
  const ends = interfaceCatalogEndpoints(row);
  if (ends?.source?.name && ends?.target?.name) {
    const flow = `${ends.source.name} → ${ends.target.name}`;
    const existingId = String(row?.existing_interface_id || "").trim();
    if (existingId) {
      return `Use existing interface · ${flow} (${name})`;
    }
    return `Interface: ${flow} (${name})`;
  }
  if (kind === "technology_unassigned" || kind === "implementation_unassigned") {
    const peerType = String(row?.implementation_peer_type || row?.entity_type || "").trim();
    const peerName = String(
      row?.implementation_peer_name ||
        row?.technology_component_name ||
        row?.platform_name ||
        "Peer"
    ).trim();
    const label = peerType === "platform" ? "platform" : "technology";
    return `App uses ${peerName} (${label}, not linked to a component yet)`;
  }
  const existingId = String(row?.existing_interface_id || "").trim();
  if (existingId) {
    return `Use existing interface: ${name}`;
  }
  return `Interface: ${name}`;
}

/**
 * Normalize interface catalog ends for review display.
 * Data-flow direction: Source = provider, Target = consumer.
 * When rationale/detail clearly states the opposite direction, re-orient.
 */
export function interfaceCatalogEndpoints(row, preview = null, reviewEntity = null) {
  if (!row || typeof row !== "object") return null;
  const kind = String(row.kind || "").trim();
  const looksLikeInterface =
    kind === "interface_from_flow" ||
    Boolean(row.provider_application_name || row.consumer_application_name);
  if (!looksLikeInterface) return null;

  const aligned = alignInterfaceRowWithDataFlow(row);
  const sourceName = String(
    aligned.provider_application_name || aligned.provider_name || ""
  ).trim();
  const targetName = String(
    aligned.consumer_application_name || aligned.consumer_name || ""
  ).trim();
  const sourceBaseType = aligned.provider_entity_type || row.provider_entity_type || "application";
  const targetBaseType = aligned.consumer_entity_type || row.consumer_entity_type || "application";

  if (!sourceName && !targetName) return null;

  const sourceType = effectiveCatalogSurfaceType(
    preview,
    sourceName,
    sourceBaseType,
    reviewEntity
  );
  const targetType = effectiveCatalogSurfaceType(
    preview,
    targetName,
    targetBaseType,
    reviewEntity
  );

  return {
    interfaceName: String(
      aligned.suggested_name || aligned.interface_name || row.suggested_name || row.interface_name || ""
    ).trim(),
    source: sourceName
      ? {
          name: sourceName,
          type: sourceType,
          role: "source",
          label: "Source",
        }
      : null,
    target: targetName
      ? {
          name: targetName,
          type: targetType,
          role: "target",
          label: "Target",
        }
      : null,
  };
}

function normIfaceLabel(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function ifaceLabelMatches(endName, mention) {
  const end = normIfaceLabel(endName);
  const ment = normIfaceLabel(mention);
  if (!end || !ment) return false;
  if (end === ment) return true;
  if (end.includes(ment) || ment.includes(end)) return true;
  const parts = end.split(/[^a-z0-9]+/).filter(Boolean);
  if (parts.length >= 2) {
    const initials = parts.map((p) => p[0]).join("");
    if (initials === ment.replace(/\s+/g, "")) return true;
  }
  return false;
}

function inferFlowSourceTarget(provider, consumer, ...texts) {
  const blob = texts
    .map((t) => String(t || "").trim())
    .filter(Boolean)
    .join(" ");
  if (!blob) return null;

  const patterns = [
    /(?:submission of\s+.+?\s+from|from)\s+(.+?)\s+to\s+(.+?)(?:\s+via|\s+over|\s+using|\.|$)/gi,
    /(.+?)\s+submit(?:s|ted)?\s+.+?\s+to\s+(.+?)(?:\s+via|\s+over|\s+using|\.|$)/gi,
    /(.+?)\s+(?:sends?|sent|routes?|routed|publishes?|published)\s+.+?\s+to\s+(.+?)(?:\s+via|\s+over|\s+using|\.|$)/gi,
  ];
  const candidates = [];
  for (const rx of patterns) {
    let m;
    while ((m = rx.exec(blob)) !== null) {
      candidates.push([m[1].replace(/^[\s.,;:]+|[\s.,;:]+$/g, ""), m[2].replace(/^[\s.,;:]+|[\s.,;:]+$/g, "")]);
    }
  }

  for (const [srcM, tgtM] of candidates) {
    const srcIsProv = ifaceLabelMatches(provider, srcM);
    const srcIsCons = ifaceLabelMatches(consumer, srcM);
    const tgtIsProv = ifaceLabelMatches(provider, tgtM);
    const tgtIsCons = ifaceLabelMatches(consumer, tgtM);
    if (srcIsProv && tgtIsCons) return [provider, consumer];
    if (srcIsCons && tgtIsProv) return [consumer, provider];
  }
  return null;
}

/** Re-orient interface stub ends to match described data flow (Source→Target). */
export function alignInterfaceRowWithDataFlow(row) {
  if (!row || typeof row !== "object") return row;
  const prov = String(row.provider_application_name || row.provider_name || "").trim();
  const cons = String(row.consumer_application_name || row.consumer_name || "").trim();
  if (!prov || !cons) return row;
  const oriented = inferFlowSourceTarget(
    prov,
    cons,
    row.data_flow_detail,
    row.rationale,
    row.why
  );
  if (!oriented) return row;
  const [src, tgt] = oriented;
  if (src.toLowerCase() === prov.toLowerCase() && tgt.toLowerCase() === cons.toLowerCase()) {
    return row;
  }
  const out = {
    ...row,
    provider_application_name: src,
    consumer_application_name: tgt,
  };
  if (row.provider_name) out.provider_name = src;
  if (row.consumer_name) out.consumer_name = tgt;
  const iname = String(row.suggested_name || row.interface_name || "").trim();
  if (iname) {
    const escapedProv = prov.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const escapedCons = cons.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const swapped = iname.replace(
      new RegExp(`${escapedProv}\\s*→\\s*${escapedCons}`, "i"),
      `${src} → ${tgt}`
    );
    if (swapped !== iname) {
      if (row.interface_name) out.interface_name = swapped;
      if (row.suggested_name) out.suggested_name = swapped;
    } else {
      const proto = iname.match(/\(([^)]+)\)\s*$/);
      const rebuilt = proto && !proto[1].includes("→")
        ? `${src} → ${tgt} (${proto[1]})`
        : `${src} → ${tgt}`;
      if (row.interface_name) out.interface_name = rebuilt;
      if (row.suggested_name) out.suggested_name = rebuilt;
    }
  }
  return out;
}

export function relationshipHasCatalogMatches(item) {
  if (!item || item.type !== "relationship" || !item.row) return false;
  const relEff = augmentRelMissingEndpoint(item.row);
  return (
    Array.isArray(relEff?.candidate_matches) && relEff.candidate_matches.length > 0
  );
}

export function resolutionCandidateMatchesFor(preview, surfaceName, entityType) {
  const missName = String(surfaceName || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[\\/|]+/g, " ")
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const missType = String(entityType || "").trim().toLowerCase();
  if (!missName || !missType) return [];
  const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];
  const overrides = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
  const normalizeSurface = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/&/g, " and ")
      .replace(/[\\/|]+/g, " ")
      .replace(/[_-]+/g, " ")
      .replace(/[^a-z0-9\s]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const normalizeType = (value) => String(value || "").trim().toLowerCase();

  const exact = rows.find(
    (row) =>
      normalizeSurface(row?.surface_name) === missName &&
      normalizeType(row?.entity_type) === missType
  );
  if (exact && Array.isArray(exact.candidate_matches)) {
    return exact.candidate_matches;
  }

  const overrideMatch = rows.find((row) => {
    if (normalizeSurface(row?.surface_name) !== missName) return false;
    const rosterKey = `resolution:${resolutionKey(row)}`;
    const effectiveType = normalizeType(overrides[rosterKey] ?? row?.entity_type);
    return effectiveType === missType;
  });
  if (overrideMatch && Array.isArray(overrideMatch.candidate_matches)) {
    return overrideMatch.candidate_matches;
  }

  const bySurface = rows.filter(
    (row) =>
      normalizeSurface(row?.surface_name) === missName &&
      row?.kind !== "linked_existing"
  );
  if (bySurface.length === 1 && Array.isArray(bySurface[0]?.candidate_matches)) {
    return bySurface[0].candidate_matches;
  }

  return [];
}

/** Blocked queue cards can still run "Use Existing" when an interface id or rel match exists. */
export function reviewPrimaryActionAllowedWhenBlocked(item) {
  if (!item?.blocked) return true;
  if (
    item.type === "catalog" &&
    Boolean(String(item.row?.existing_interface_id || "").trim())
  ) {
    return true;
  }
  if (item.type === "relationship" && relationshipHasCatalogMatches(item)) {
    return true;
  }
  return false;
}

/**
 * True when both ends of an interface_from_flow stub are still applications
 * after steward type overrides (interfaces are app↔app only).
 */
export function interfaceStubEndpointsAreApplications(
  row,
  preview,
  editingEntity = null
) {
  if (!row || String(row.kind || "").trim() !== "interface_from_flow") {
    return true;
  }
  const provType = interfaceFromFlowEndpointType(
    row,
    "provider",
    preview,
    editingEntity
  );
  const consType = interfaceFromFlowEndpointType(
    row,
    "consumer",
    preview,
    editingEntity
  );
  return (
    normalizeTypeForCompare(provType) === "application" &&
    normalizeTypeForCompare(consType) === "application"
  );
}

/**
 * True when the card is blocked and offers no useful primary/secondary action (Skip-only).
 * These should be hidden and auto-discarded rather than shown to stewards.
 * Interface stubs whose endpoints are no longer applications are always dead.
 */
export function isReviewQueueItemDead(item, preview = null, editingEntity = null) {
  if (!item || item.type === "name_match" || item.type === "description") return false;
  if (
    item.type === "catalog" &&
    String(item.row?.kind || "").trim() === "interface_from_flow" &&
    preview &&
    !interfaceStubEndpointsAreApplications(item.row, preview, editingEntity)
  ) {
    return true;
  }
  if (!item.blocked) return false;
  if (reviewPrimaryActionAllowedWhenBlocked(item)) return false;
  const labels = getReviewCardActionLabels(item);
  if (labels.secondary) return false;
  // Don't prune interface cards that still wait on an unresolved name-match card.
  if (preview && catalogLinkDeferredForNameMatch(item.row, preview)) return false;
  return true;
}

function catalogLinkDeferredForNameMatch(row, preview) {
  const kind = String(row?.kind || "").trim();
  if (kind !== "interface_from_flow") {
    return false;
  }
  const names = [
    row?.suggested_name,
    row?.provider_application_name,
    row?.consumer_application_name,
  ];
  const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];
  for (const name of names) {
    if (!String(name || "").trim()) continue;
    const hasPending = rows.some(
      (r) =>
        isNameMatchReviewRow(r, preview) &&
        normName(r?.surface_name) === normName(name) &&
        !nameMatchReviewDecision(preview, r)
    );
    if (hasPending) return true;
  }
  return false;
}

export { resolutionDecidedType } from "./enrichmentEntityRoster";

// Once both app endpoints have map ids, interface_from_flow stubs are
// auto-written (see EnrichReviewDialog autoCompleteReadyCatalogConnections).
const AUTO_CREATE_CATALOG_KINDS = new Set(["interface_from_flow"]);

/**
 * True when a named catalog endpoint already has a map entity id.
 * kept_new / confirmed alone do not count — classifyMissingSurface still
 * uses resolution decisions for gap and pending_create messaging.
 */
function catalogEndpointIdentitySettled(preview, surfaceName, entityType, entityId) {
  return Boolean(String(entityId || "").trim());
}

/**
 * Connection stubs ready to write without another steward click — both
 * provider and consumer applications already have map entity ids.
 */
export function catalogStubReadyForAutoCreate(row, preview, editingEntity = null, idx = -1) {
  if (!row || !preview) return false;
  const kind = String(row.kind || "").trim();
  if (!AUTO_CREATE_CATALOG_KINDS.has(kind)) return false;
  if (catalogLinkDeferredForNameMatch(row, preview)) return false;
  if (idx >= 0 && catalogReviewDecision(preview, row, idx)) return false;
  const gap = catalogGapStatus(row, preview, editingEntity);
  if (gap.blocked) return false;

  const provType = effectiveCatalogSurfaceType(
    preview,
    row.provider_application_name,
    row.provider_entity_type || "application",
    editingEntity
  );
  const consType = effectiveCatalogSurfaceType(
    preview,
    row.consumer_application_name,
    row.consumer_entity_type || "application",
    editingEntity
  );
  if (provType !== "application" || consType !== "application") return false;
  if (!String(row.provider_entity_id || "").trim()) return false;
  if (!String(row.consumer_entity_id || "").trim()) return false;
  return (
    catalogEndpointIdentitySettled(
      preview,
      row.provider_application_name,
      provType,
      row.provider_entity_id
    ) &&
    catalogEndpointIdentitySettled(
      preview,
      row.consumer_application_name,
      consType,
      row.consumer_entity_id
    )
  );
}

/**
 * Effective endpoint type for an interface_from_flow catalog row end.
 */
export function interfaceFromFlowEndpointType(
  row,
  role,
  preview,
  editingEntity = null
) {
  const name =
    role === "consumer"
      ? row?.consumer_application_name
      : row?.provider_application_name;
  const base =
    role === "consumer"
      ? row?.consumer_entity_type || "application"
      : row?.provider_entity_type || "application";
  return effectiveCatalogSurfaceType(preview, name, base, editingEntity);
}

/**
 * Catalog stubs ready to auto-write after name matching (both ends on map).
 */
export function listAutoCompletableCatalogStubs(preview, editingEntity = null) {
  if (!preview) return [];
  const hydrated = refreshSolutionStructureCatalogRows(preview);
  const stubs = Array.isArray(hydrated.proposed_catalog_entities)
    ? hydrated.proposed_catalog_entities
    : [];
  const out = [];
  stubs.forEach((row, idx) => {
    if (!row || typeof row !== "object") return;
    if (isHiddenReviewCatalogRow(row)) return;
    if (!catalogStubReadyForAutoCreate(row, hydrated, editingEntity, idx)) return;
    out.push({
      row,
      idx,
      key: catalogStubKey(row, idx),
      priority: reviewQueueItemPriority({ type: "catalog", row }),
    });
  });
  out.sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50) || a.idx - b.idx);
  return out;
}

/** F: defer relationship cards until related name matching is decided. */
export function relationshipDeferredForNameMatch(rel, preview) {
  if (!rel || !preview) return false;
  const r = augmentRelMissingEndpoint(rel);
  const names = [
    r?.missing_surface_name,
    r?.surface_target_name,
    r?.surface_source_name,
    r?.surface_other_application,
    r?.surface_child_application,
  ];
  const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];
  for (const name of names) {
    if (!String(name || "").trim()) continue;
    const hasPending = rows.some(
      (row) =>
        isNameMatchReviewRow(row, preview) &&
        normName(row?.surface_name) === normName(name) &&
        !nameMatchReviewDecision(preview, row)
    );
    if (hasPending) return true;
  }
  return false;
}

export function setReviewRelationshipOutcome(prev, key, status) {
  if (!prev || !key) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const pr = { ...(outcomes.proposed_relationships || {}) };
  pr[key] = status;
  return {
    ...prev,
    enrichment_review_outcomes: { ...outcomes, proposed_relationships: pr },
  };
}

export function setReviewCatalogOutcome(prev, catalogKey, status) {
  if (!prev || !catalogKey) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const ce = { ...(outcomes.catalog_entities || {}) };
  ce[catalogKey] = status;
  return {
    ...prev,
    enrichment_review_outcomes: { ...outcomes, catalog_entities: ce },
  };
}

function catalogRowTouchesSurface(row, surfaceNorm) {
  if (!row || !surfaceNorm) return false;
  const fields = [
    "suggested_name",
    "interface_name",
    "technology_component_name",
    "implementation_peer_name",
    "platform_name",
    "provider_application_name",
    "consumer_application_name",
  ];
  return fields.some((field) => normName(row[field]) === surfaceNorm);
}

function relationshipTouchesSurface(rel, surfaceNorm) {
  const r = augmentRelMissingEndpoint(rel);
  const fields = [
    "missing_surface_name",
    "surface_target_name",
    "surface_source_name",
    "surface_other_application",
    "surface_child_application",
  ];
  return fields.some((field) => normName(r?.[field]) === surfaceNorm);
}

/**
 * After a name-match Skip, discard pending relationships/catalog stubs that depend on that name.
 */
export function cascadeDiscardAfterNameMatchSkip(prev, surfaceName, entityType = "") {
  if (!prev) return prev;
  const surfaceNorm = normName(surfaceName);
  if (!surfaceNorm) return prev;
  let next = prev;

  const proposed = Array.isArray(prev.proposed_relationships)
    ? prev.proposed_relationships
    : [];
  proposed.forEach((rel, idx) => {
    if (!rel || typeof rel !== "object") return;
    if (HIDDEN_REVIEW_REL_KINDS.has(String(rel?.kind || "").trim())) return;
    if (String(rel?.existing_relationship_id || "").trim()) return;
    if (relationshipReviewDecision(next, rel, idx)) return;
    if (!relationshipTouchesSurface(rel, surfaceNorm)) return;
    const eff = augmentRelMissingEndpoint(rel);
    const miss = normName(eff?.missing_surface_name);
    // Only cascade when the skipped name is still an unresolved endpoint.
    if (miss && miss === surfaceNorm) {
      next = setReviewRelationshipOutcome(next, `p${idx}`, "discarded");
    }
  });

  const stubs = Array.isArray(prev.proposed_catalog_entities)
    ? prev.proposed_catalog_entities
    : [];
  stubs.forEach((row, idx) => {
    if (!row || typeof row !== "object") return;
    if (isHiddenReviewCatalogRow(row)) return;
    if (catalogReviewDecision(next, row, idx)) return;
    if (!catalogRowTouchesSurface(row, surfaceNorm)) return;
    const gap = catalogGapStatus(row, next);
    // Discard if this stub still has a gap involving the skipped surface.
    if (!gap.blocked && !gap.message) return;
    const stillTouchesUnresolved =
      (!String(row.provider_entity_id || "").trim() &&
        normName(row.provider_application_name) === surfaceNorm) ||
      (!String(row.consumer_entity_id || "").trim() &&
        normName(row.consumer_application_name) === surfaceNorm) ||
      (!String(
        row.implementation_peer_entity_id ||
          row.technology_component_entity_id ||
          row.platform_entity_id ||
          ""
      ).trim() &&
        (normName(row.implementation_peer_name) === surfaceNorm ||
          normName(row.technology_component_name) === surfaceNorm ||
          normName(row.platform_name) === surfaceNorm));
    if (stillTouchesUnresolved) {
      next = setReviewCatalogOutcome(next, catalogStubKey(row, idx), "discarded");
    }
  });

  return next;
}

/**
 * Persist discard outcomes for Skip-only (dead) pending cards so they leave Needs review.
 */
export function pruneDeadReviewCards(prev) {
  if (!prev) return prev;
  const hydrated = refreshSolutionStructureCatalogRows(prev);
  let next = prev;
  let changed = false;

  const proposed = Array.isArray(hydrated.proposed_relationships)
    ? hydrated.proposed_relationships
    : [];
  proposed.forEach((rel, idx) => {
    if (!rel || typeof rel !== "object") return;
    if (HIDDEN_REVIEW_REL_KINDS.has(String(rel?.kind || "").trim())) return;
    if (String(rel?.existing_relationship_id || "").trim()) return;
    if (relationshipReviewDecision(next, rel, idx)) return;
    const gap = relationshipGapStatus(rel, hydrated);
    const item = {
      type: "relationship",
      row: rel,
      idx,
      blocked: Boolean(gap.blocked),
      blockedMessage: gap.message || "",
    };
    if (!isReviewQueueItemDead(item, hydrated)) return;
    next = setReviewRelationshipOutcome(next, `p${idx}`, "discarded");
    changed = true;
  });

  const stubs = Array.isArray(hydrated.proposed_catalog_entities)
    ? hydrated.proposed_catalog_entities
    : [];
  stubs.forEach((row, idx) => {
    if (!row || typeof row !== "object") return;
    if (isHiddenReviewCatalogRow(row)) return;
    if (catalogReviewDecision(next, row, idx)) return;
    const gap = catalogGapStatus(row, hydrated);
    const item = {
      type: "catalog",
      row,
      idx,
      blocked: Boolean(gap.blocked),
      blockedMessage: gap.message || "",
    };
    if (!isReviewQueueItemDead(item, hydrated)) return;
    next = setReviewCatalogOutcome(next, catalogStubKey(row, idx), "discarded");
    changed = true;
  });

  return changed ? next : prev;
}

function reviewQueueItemCategory(item) {
  if (!item) return "new";
  if (item.type === "name_match") return "needs_review";
  if (item.type === "catalog") {
    if (Boolean(String(item.row?.existing_interface_id || "").trim())) {
      return "catalog_match";
    }
    return item.blocked ? "needs_review" : "new";
  }
  if (item.type === "relationship") {
    return item.blocked ? "needs_review" : "new";
  }
  return "new";
}

/**
 * Human-friendly decision labels for the review queue card.
 * Button order is always: Add to map → Link to existing → Skip (when each applies).
 */
export function getReviewCardActionLabels(item) {
  const discard = "Skip";
  const busy = "Saving…";
  const linkExisting = "Link to existing";
  const addToMap = "Add to map";

  if (!item) {
    return {
      addLabel: addToMap,
      linkLabel: linkExisting,
      primary: addToMap,
      secondary: linkExisting,
      discard,
      busy,
      hint: null,
      primaryHint: "If it does not exist yet.",
      secondaryHint:
        "If this is a duplicate already on your map. Click the button to see potential matches.",
      discardHint: "If you do not want it on the map.",
    };
  }

  if (item.type === "description") {
    return {
      addLabel: "Update description",
      linkLabel: null,
      primary: "Update description",
      secondary: null,
      discard,
      busy,
      hint: null,
      primaryHint: "Applies the AI text to this application.",
      discardHint: "Leave the current description unchanged.",
    };
  }

  if (item.type === "name_match") {
    return {
      addLabel: addToMap,
      linkLabel: linkExisting,
      primary: addToMap,
      secondary: linkExisting,
      discard: "Skip",
      busy,
      hint: null,
      primaryHint: "If it does not exist yet.",
      secondaryHint:
        "If this is a duplicate already on your map. Click the button to see potential matches.",
      discardHint: "If you do not want it on the map.",
    };
  }

  if (item.type === "catalog") {
    const hasExisting = Boolean(String(item.row?.existing_interface_id || "").trim());
    if (hasExisting) {
      return {
        addLabel: addToMap,
        linkLabel: linkExisting,
        primary: addToMap,
        secondary: linkExisting,
        discard,
        busy,
        hint: null,
        primaryHint: "Creates a new connection on your map.",
        secondaryHint: "Uses the connection already on your map.",
        discardHint: "Leave this connection off the map.",
      };
    }
    if (item.blocked) {
      return {
        addLabel: addToMap,
        linkLabel: linkExisting,
        primary: addToMap,
        secondary: linkExisting,
        discard,
        busy,
        hint: null,
        primaryHint: "Creates what is missing, then adds this connection.",
        secondaryHint: "Match ends that are already on your map.",
        discardHint: "Leave this connection off the map.",
      };
    }
    if (item.blockedMessage) {
      return {
        addLabel: addToMap,
        linkLabel: null,
        primary: addToMap,
        secondary: null,
        discard,
        busy,
        hint: null,
        primaryHint: "Creates missing apps marked earlier as new with this connection.",
        discardHint: "Leave this connection off the map.",
      };
    }
    return {
      addLabel: addToMap,
      linkLabel: null,
      primary: addToMap,
      secondary: null,
      discard,
      busy,
      hint: null,
      primaryHint: "Add this connection to your map.",
      discardHint: "Leave this connection off the map.",
    };
  }

  if (item.type === "relationship") {
    if (item.blocked) {
      return {
        addLabel: addToMap,
        linkLabel: linkExisting,
        primary: addToMap,
        secondary: linkExisting,
        discard,
        busy,
        hint: null,
        primaryHint: relationshipHasCatalogMatches(item)
          ? "If the missing end is new."
          : "Create the missing end, then add this association.",
        secondaryHint: relationshipHasCatalogMatches(item)
          ? "If the missing end is already on your map."
          : "Match the missing end to something already on your map.",
        discardHint: "Leave this association off the map.",
      };
    }
    if (item.blockedMessage) {
      return {
        addLabel: addToMap,
        linkLabel: null,
        primary: addToMap,
        secondary: null,
        discard,
        busy,
        hint: null,
        primaryHint: "Creates names you marked as new when you add this association.",
        discardHint: "Leave this association off the map.",
      };
    }
    return {
      addLabel: addToMap,
      linkLabel: null,
      primary: addToMap,
      secondary: null,
      discard,
      busy,
      hint: null,
      primaryHint: "Add this association to your map.",
      discardHint: "Leave this association off the map.",
    };
  }

  return {
    addLabel: addToMap,
    linkLabel: null,
    primary: addToMap,
    secondary: null,
    discard,
    busy,
    hint: null,
    primaryHint: "If this is new.",
    discardHint: "If you do not want it on the map.",
  };
}

/** True only when this queue card already has a confirmed catalog match behind it. */
export function reviewQueuePrimaryIsUseExisting(item) {
  return reviewQueueItemCategory(item) === "catalog_match";
}

/** Pending review item still needs steward action, even if AI found candidates. */
export function reviewQueueItemNeedsAction(item) {
  const category = reviewQueueItemCategory(item);
  return category === "new" || category === "needs_review";
}

/** Pending review item that is genuinely net-new, not just unresolved. */
export function reviewQueueItemIsNewFinding(item) {
  return reviewQueueItemCategory(item) === "new";
}

/** Pending review item is unresolved / blocked and still needs explicit mapping or create decision. */
export function reviewQueueItemNeedsReview(item) {
  return reviewQueueItemCategory(item) === "needs_review";
}

export function countReviewQueueNewFindings(queue) {
  const items = Array.isArray(queue) ? queue : [];
  let newCount = 0;
  let actionableCount = 0;
  let needsReviewCount = 0;
  let newRelationships = 0;
  let newCatalog = 0;
  let hasDescription = false;
  items.forEach((item) => {
    if (reviewQueueItemNeedsAction(item)) {
      actionableCount += 1;
    }
    if (reviewQueueItemNeedsReview(item)) {
      needsReviewCount += 1;
    }
    if (reviewQueueItemIsNewFinding(item)) {
      newCount += 1;
      if (item.type === "relationship") newRelationships += 1;
      else if (item.type === "catalog") newCatalog += 1;
      else if (item.type === "description") hasDescription = true;
    }
  });
  const total = items.length;
  const existingMatchCount = total - actionableCount;
  return {
    total,
    newCount,
    actionableCount,
    needsReviewCount,
    existingMatchCount,
    newRelationships,
    newCatalog,
    hasDescription,
  };
}

/** User-facing summary of new, unresolved, and confirmed catalog-matched pending findings. */
export function formatReviewQueueFindingsSummary(counts) {
  if (!counts || !counts.total) return "";

  const {
    newCount,
    needsReviewCount,
    existingMatchCount,
    newRelationships,
    newCatalog,
    hasDescription,
  } = counts;

  if (newCount === 0 && needsReviewCount === 0 && existingMatchCount > 0) {
    const n = existingMatchCount;
    return `All ${n} item${n === 1 ? "" : "s"} already match something on your map.`;
  }

  if (newCount > 0 && needsReviewCount === 0 && existingMatchCount === 0) {
    return `We found ${formatNewFindingsPhrase(counts)}.`;
  }

  const parts = [];
  if (newCount > 0) {
    parts.push(`We found ${formatNewFindingsPhrase(counts)}.`);
  }
  if (needsReviewCount > 0) {
    parts.push(
      `${needsReviewCount} item${
        needsReviewCount === 1 ? "" : "s"
      } need a quick match before you can approve them.`
    );
  }
  if (existingMatchCount > 0) {
    parts.push(
      `${existingMatchCount} item${
        existingMatchCount === 1 ? "" : "s"
      } already ${existingMatchCount === 1 ? "matches" : "match"} your map.`
    );
  }
  if (parts.length) return parts.join(" ");

  return "";
}

function formatNewFindingsPhrase({
  newCount,
  newRelationships,
  newCatalog,
  hasDescription,
}) {
  const parts = [];
  if (newRelationships > 0) {
    parts.push(
      `${newRelationships} new relationship${newRelationships === 1 ? "" : "s"}`
    );
  }
  if (newCatalog > 0) {
    parts.push(`${newCatalog} new interface${newCatalog === 1 ? "" : "s"}`);
  }
  if (hasDescription) {
    parts.push("an app description update");
  }
  if (!parts.length) {
    return `${newCount} new item${newCount === 1 ? "" : "s"}`;
  }
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

export function filterReviewQueue(queue, mode = "all") {
  const items = Array.isArray(queue) ? queue : [];
  if (mode !== "new") return items;
  return items.filter(reviewQueueItemNeedsAction);
}

/** Pending items that already have a concrete catalog match identified. */
export function filterCatalogMatchQueueItems(queue) {
  return (Array.isArray(queue) ? queue : []).filter(reviewQueuePrimaryIsUseExisting);
}

/**
 * Queue cards safe to accept in one click (AI recommendations that do not need
 * name-match judgment). Excludes blocked / needs-review cards.
 */
export function filterAcceptableAiRecommendationItems(queue) {
  return (Array.isArray(queue) ? queue : []).filter((item) => {
    if (!item || item.blocked) return false;
    if (item.type === "name_match") return false;
    if (item.type === "description") return true;
    if (item.type === "relationship" || item.type === "catalog") {
      return !reviewQueueItemNeedsReview(item);
    }
    return false;
  });
}

/**
 * Title and body copy for the universal review overview card.
 * Same layout for every job start; wording varies by pending counts and prior decisions.
 */
export function formatReviewOverviewContent(counts, { decidedCount = 0 } = {}) {
  const total = counts?.total ?? 0;
  const summary = formatReviewQueueFindingsSummary(counts);

  if (total === 0) {
    return {
      title: "What we found",
      body:
        decidedCount > 0
          ? "You have reviewed everything from this upload."
          : "Nothing left to review from this upload.",
      variant: "empty",
    };
  }

  let body = summary;
  if (decidedCount > 0) {
    body = `${decidedCount} done · ${total} left. ${summary}`;
  }

  return {
    title: "What we found",
    body,
    variant:
      counts.actionableCount === 0
        ? "all_catalog_match"
        : counts.existingMatchCount === 0 && counts.needsReviewCount === 0
          ? "all_new"
          : "mixed",
  };
}

function isRelResolvable(rel) {
  const sid = String(rel?.source_entity_id || "").trim();
  const tid = String(rel?.target_entity_id || "").trim();
  return Boolean(sid && tid);
}

function relationshipGapStatus(rel, preview = null) {
  const eff = augmentRelMissingEndpoint(rel);
  if (isRelResolvable(eff)) return { blocked: false, message: "" };
  const name = String(eff?.missing_surface_name || "").trim();
  const type = String(eff?.missing_entity_type || "").trim();
  if (name && type) {
    const part = classifyMissingSurface(
      preview,
      name,
      type,
      formatCatalogEntityTypeLabel(type)
    );
    return composeGapMessages(part ? [part] : []);
  }
  return {
    blocked: true,
    message: "One or both systems are not on your map yet.",
  };
}

function relationshipBlockedMessage(rel, preview = null) {
  const gap = relationshipGapStatus(rel, preview);
  return gap.blocked ? gap.message : "";
}

function catalogGapStatus(row, preview = null, editingEntity = null) {
  const kind = String(row?.kind || "").trim();
  if (kind === "technology_unassigned" || kind === "implementation_unassigned") {
    return { blocked: false, message: "" };
  }
  if (String(row?.existing_interface_id || "").trim()) {
    return { blocked: false, message: "" };
  }
  const parts = [];
  if (!String(row?.provider_entity_id || "").trim()) {
    const provType = effectiveCatalogSurfaceType(
      preview,
      row?.provider_application_name,
      row?.provider_entity_type || "application",
      editingEntity
    );
    parts.push(
      classifyMissingSurface(
        preview,
        row?.provider_application_name,
        provType,
        `Source ${formatCatalogEntityTypeLabel(provType)}`
      )
    );
  }
  if (!String(row?.consumer_entity_id || "").trim()) {
    const consType = effectiveCatalogSurfaceType(
      preview,
      row?.consumer_application_name,
      row?.consumer_entity_type || "application",
      editingEntity
    );
    parts.push(
      classifyMissingSurface(
        preview,
        row?.consumer_application_name,
        consType,
        `Target ${formatCatalogEntityTypeLabel(consType)}`
      )
    );
  }
  return composeGapMessages(parts.filter(Boolean));
}

function catalogBlockedMessageForRow(row, preview = null) {
  const gap = catalogGapStatus(row, preview);
  return gap.blocked ? gap.message : "";
}

function catalogBlockedMessage(row, preview = null) {
  return catalogBlockedMessageForRow(row, preview);
}

/**
 * @param {object} params
 * @returns {import('./enrichmentReviewQueue').ReviewQueueItem[]}
 */
export function buildReviewQueue({
  preview,
  editingEntity,
  relActions = {},
  catalogActions = {},
  matchActions = {},
  descriptionApplied = false,
}) {
  if (!preview) return [];
  const hydrated = refreshSolutionStructureCatalogRows(preview);
  const anchor = editingEntity?.name || hydrated.anchor_name || "Application";
  const out = [];

  const resolution = Array.isArray(hydrated.resolution) ? hydrated.resolution : [];
  resolution.forEach((row, idx) => {
    if (!isNameMatchReviewRow(row, hydrated, editingEntity)) return;
    if (nameMatchReviewDecision(hydrated, row, matchActions)) return;
    if (normName(row.entity_type) === "solution-component") return;
    const rk = resolutionKey(row);
    const surface = String(row.surface_name || "").trim();
    const et = String(row.entity_type || "application").trim();
    out.push({
      id: `match-${rk}`,
      type: "name_match",
      claim: surface,
      row,
      idx,
      resolutionKey: rk,
      blocked: false,
      blockedMessage: "",
      kindLabel: "name matching",
    });
  });

  const proposed = Array.isArray(hydrated.proposed_relationships)
    ? hydrated.proposed_relationships
    : [];
  proposed.forEach((rel, idx) => {
    if (!rel || typeof rel !== "object") return;
    if (HIDDEN_REVIEW_REL_KINDS.has(String(rel?.kind || "").trim())) return;
    if (String(rel?.existing_relationship_id || "").trim()) return;
    if (relRowComplete(hydrated, rel, idx, relActions)) return;
    // Steward already approved — save still running; not a review card.
    if (relationshipReviewInFlight(hydrated, rel, idx, relActions)) return;
    // F: wait for name matching on the peer first.
    if (relationshipDeferredForNameMatch(rel, hydrated)) return;
    const gap = relationshipGapStatus(rel, hydrated);
    const item = {
      id: `rel-${idx}`,
      type: "relationship",
      claim: compactRelationshipClaim(anchor, rel),
      row: rel,
      idx,
      blocked: Boolean(gap.blocked),
      blockedMessage: gap.message || "",
      kindLabel: relationshipKindLabel(rel),
      proposalSource: String(rel?.proposal_source || "").trim().toLowerCase(),
      isCatalogRecommendation: isCatalogRecommendationRel(rel),
    };
    if (!isReviewQueueItemDead(item, hydrated)) out.push(item);
  });

  const stubs = Array.isArray(hydrated.proposed_catalog_entities)
    ? hydrated.proposed_catalog_entities
    : [];
  stubs.forEach((row, idx) => {
    if (!row || typeof row !== "object") return;
    if (isHiddenReviewCatalogRow(row)) return;
    if (catalogRowComplete(hydrated, row, idx, catalogActions)) return;
    // Steward already chose Add to map — create still running; not a review card.
    if (catalogReviewInFlight(hydrated, row, idx, catalogActions)) return;
    const kind = String(row.kind || "").trim();
    // App↔app interfaces only — drop stubs once an endpoint is retyped away from application.
    if (
      kind === "interface_from_flow" &&
      !interfaceStubEndpointsAreApplications(row, hydrated, editingEntity)
    ) {
      return;
    }
    // Keep app↔app interface cards visible through name matching; once both
    // ends are confirmed they stay visible briefly while auto-create runs
    // (or until the steward Adds / Skips if auto-create is deferred).
    if (catalogLinkDeferredForNameMatch(row, hydrated)) {
      if (kind !== "interface_from_flow") return;
    }
    if (
      kind !== "interface_from_flow" &&
      catalogStubReadyForAutoCreate(row, hydrated, editingEntity, idx)
    ) {
      return;
    }
    const gap = catalogGapStatus(row, hydrated, editingEntity);
    let blockedMessage = gap.message || "";
    if (
      kind === "interface_from_flow" &&
      catalogLinkDeferredForNameMatch(row, hydrated)
    ) {
      blockedMessage =
        "Waiting on name matching for apps in this connection — finish those first.";
    }
    const item = {
      id: `cat-${catalogStubKey(row, idx)}`,
      type: "catalog",
      claim: compactCatalogClaim(row),
      row,
      idx,
      blocked: Boolean(gap.blocked) || catalogLinkDeferredForNameMatch(row, hydrated),
      blockedMessage,
      kindLabel: "connection",
    };
    if (!isReviewQueueItemDead(item, hydrated, editingEntity)) out.push(item);
  });

  const desc = hydrated.description_proposal;
  if (desc?.proposed_description && !descriptionApplied) {
    out.push({
      id: "description",
      type: "description",
      claim: "Update app description",
      row: desc,
      idx: -1,
      blocked: false,
      blockedMessage: "",
      kindLabel: "description",
    });
  }

  out.sort((a, b) => {
    const pa = reviewQueueItemPriority(a);
    const pb = reviewQueueItemPriority(b);
    if (pa !== pb) return pa - pb;
    if (a.type === "catalog" && b.type === "catalog") {
      return (a.idx ?? 0) - (b.idx ?? 0);
    }
    if (a.blocked !== b.blocked) return a.blocked ? 1 : -1;
    return 0;
  });
  return out;
}

/**
 * Read-only traceability list for items already accepted or discarded (includes persisted outcomes).
 */
export function buildDecidedReviewItems({
  preview,
  editingEntity,
  relActions = {},
  catalogActions = {},
  descriptionApplied = false,
}) {
  if (!preview) return [];
  const anchor = editingEntity?.name || preview.anchor_name || "Application";
  const outcomes = preview?.enrichment_review_outcomes || {};
  const out = [];
  const resolution = Array.isArray(preview.resolution) ? preview.resolution : [];

  const proposed = Array.isArray(preview.proposed_relationships)
    ? preview.proposed_relationships
    : [];
  const metamodelDiscardedSet = new Set(
    Array.isArray(outcomes.relationships_discarded_by_metamodel)
      ? outcomes.relationships_discarded_by_metamodel
      : []
  );
  proposed.forEach((rel, idx) => {
    if (!rel || typeof rel !== "object") return;
    if (HIDDEN_REVIEW_REL_KINDS.has(String(rel?.kind || "").trim())) return;
    if (String(rel?.existing_relationship_id || "").trim()) return;
    const decision = relationshipReviewDecision(preview, rel, idx, relActions);
    if (!decision) return;
    const wasMetamodelDiscard = decision === "discarded" && metamodelDiscardedSet.has(idx);
    const baseClaim = compactRelationshipClaim(anchor, rel);
    out.push({
      id: `rel-${idx}`,
      type: "relationship",
      claim: wasMetamodelDiscard
        ? `${baseClaim} (removed — invalid after type change)`
        : baseClaim,
      row: rel,
      idx,
      blocked: false,
      blockedMessage: "",
      kindLabel: wasMetamodelDiscard
        ? "type change"
        : String(rel?.kind || rel?.relationship_type || "relationship").replace(/_/g, " "),
      decision,
    });
  });

  const stubs = Array.isArray(preview.proposed_catalog_entities)
    ? preview.proposed_catalog_entities
    : [];
  stubs.forEach((row, idx) => {
    if (!row || typeof row !== "object") return;
    if (isHiddenReviewCatalogRow(row)) return;
    const decision = catalogReviewDecision(preview, row, idx, catalogActions);
    if (!decision) return;
    out.push({
      id: `cat-${catalogStubKey(row, idx)}`,
      type: "catalog",
      claim: compactCatalogClaim(row),
      row,
      idx,
      blocked: false,
      blockedMessage: "",
      kindLabel: "connection",
      decision,
    });
  });

  const desc = preview.description_proposal;
  if (desc?.proposed_description) {
    if (outcomes.description_applied || descriptionApplied) {
      out.push({
        id: "description",
        type: "description",
        claim: "Update app description",
        row: desc,
        idx: -1,
        blocked: false,
        blockedMessage: "",
        kindLabel: "description",
        decision: "approved",
      });
    } else if (outcomes.description_discarded) {
      out.push({
        id: "description",
        type: "description",
        claim: "Update app description",
        row: desc,
        idx: -1,
        blocked: false,
        blockedMessage: "",
        kindLabel: "description",
        decision: "discarded",
      });
    }
  }

  resolution.forEach((row, idx) => {
    if (!isNameMatchReviewRow(row, preview, editingEntity)) return;
    const rk = resolutionKey(row);
    const st = outcomes.resolution_matches?.[rk];
    const surface = String(row.surface_name || "").trim();
    const etLabel = formatCatalogEntityTypeLabel(row.entity_type);
    if (st === "confirmed") {
      out.push({
        id: `match-${rk}`,
        type: "name_match",
        claim: `Matched "${surface}" to existing ${etLabel}`,
        row,
        idx,
        blocked: false,
        blockedMessage: "",
        kindLabel: "name matching",
        decision: "approved",
      });
    } else if (st === "kept_new") {
      out.push({
        id: `match-${rk}`,
        type: "name_match",
        claim: `Add "${surface}" as new ${etLabel}`,
        row,
        idx,
        blocked: false,
        blockedMessage: "",
        kindLabel: "name matching",
        decision: "approved",
      });
    } else if (st === "discarded") {
      out.push({
        id: `match-${rk}`,
        type: "name_match",
        claim: `Skipped name match for "${surface}"`,
        row,
        idx,
        blocked: false,
        blockedMessage: "",
        kindLabel: "name matching",
        decision: "discarded",
      });
    }
  });

  const linkedResolution = Array.isArray(preview.resolution) ? preview.resolution : [];
  linkedResolution.forEach((row, idx) => {
    if (!row || row.kind !== "linked_existing") return;
    const surface = String(row.surface_name || "").trim();
    const et = String(row.entity_type || "").trim();
    if (!surface || !et) return;
    out.push({
      id: `linked-${idx}-${surface}`,
      type: "entity_match",
      claim: `Linked "${surface}" to existing ${et.replace(/-/g, " ")}`,
      row,
      idx,
      blocked: false,
      blockedMessage: "",
      kindLabel: "entity match",
      decision: "approved",
    });
  });

  const metamodelAdj = outcomes.relationship_metamodel_adjustments || {};
  Object.entries(metamodelAdj).forEach(([key, adj]) => {
    if (!adj || adj.pending) return;
    const idxMatch = key.match(/^p(\d+)$/);
    if (!idxMatch) return;
    const relIdx = Number(idxMatch[1]);
    const rel = proposed[relIdx];
    if (!rel) return;
    const alreadyDecided = out.some((it) => it.id === `rel-${relIdx}`);
    if (alreadyDecided) return;
    if (adj.from && adj.to && adj.from !== adj.to) {
      out.push({
        id: `type-adj-${relIdx}`,
        type: "type_change_adjustment",
        claim: `${compactRelationshipClaim(anchor, rel)} (adjusted: ${adj.from} → ${adj.to})`,
        row: rel,
        idx: relIdx,
        blocked: false,
        blockedMessage: "",
        kindLabel: "type change",
        decision: "approved",
      });
    }
  });

  return out;
}

export function countQueueStats(queue, relActions, catalogActions, preview) {
  let approved = 0;
  let discarded = 0;
  const proposed = preview?.proposed_relationships || [];
  proposed.forEach((rel, idx) => {
    if (HIDDEN_REVIEW_REL_KINDS.has(String(rel?.kind || "").trim())) return;
    if (String(rel?.existing_relationship_id || "").trim()) return;
    const decision = relationshipReviewDecision(preview, rel, idx, relActions);
    if (decision === "approved") approved += 1;
    else if (decision === "discarded") discarded += 1;
  });
  const stubs = preview?.proposed_catalog_entities || [];
  stubs.forEach((row, idx) => {
    const decision = catalogReviewDecision(preview, row, idx, catalogActions);
    if (decision === "approved") approved += 1;
    else if (decision === "discarded") discarded += 1;
  });
  if (preview?.description_proposal?.proposed_description) {
    const outcomes = preview?.enrichment_review_outcomes || {};
    if (outcomes.description_applied) approved += 1;
    else if (outcomes.description_discarded) discarded += 1;
  }
  const resolution = Array.isArray(preview?.resolution) ? preview.resolution : [];
  const matchOutcomes = preview?.enrichment_review_outcomes?.resolution_matches || {};
  resolution.forEach((row) => {
    if (!isNameMatchReviewRow(row, preview)) return;
    const rk = resolutionKey(row);
    const st = matchOutcomes[rk];
    if (st === "confirmed" || st === "kept_new") approved += 1;
    else if (st === "discarded") discarded += 1;
  });
  const pending = queue.length;
  const total = pending + approved + discarded;
  return { pending, approved, discarded, total, reviewed: approved + discarded };
}

function parseJobResultDocument(job) {
  const raw = job?.result;
  if (raw == null) return null;
  let res;
  if (typeof raw === "string") {
    try {
      res = JSON.parse(raw);
    } catch {
      return null;
    }
  } else {
    res = raw;
  }
  if (!res || typeof res !== "object") return null;
  return res;
}

export function previewFromJob(job) {
  const doc = parseJobResultDocument(job);
  if (!doc) return null;
  const preview = doc.preview || null;
  if (!preview || typeof preview !== "object") return null;
  return mergeJobAuditsIntoPreview(preview, doc);
}

function unresolvedSetupMatchCount(preview, editingEntity = null) {
  const resolution = Array.isArray(preview?.resolution) ? preview.resolution : [];
  let n = 0;
  for (const row of resolution) {
    if (!isNameMatchReviewRow(row, preview, editingEntity)) continue;
    if (nameMatchReviewDecision(preview, row)) continue;
    n += 1;
  }
  return n;
}

/**
 * True while the steward still has review cards or setup mappings to act on.
 * Matches the review UI: queue empty and setup complete means review is finished
 * (roster-only gaps for newly identified entities do not keep the job in Needs review).
 */
export function previewHasOutstandingReview(preview, editingEntity = null) {
  if (!preview || typeof preview !== "object") return false;

  if (preview?.enrichment_review_outcomes?.review_complete) return false;

  if (unresolvedSetupMatchCount(preview) > 0) return true;

  const entity = editingEntity || {
    id: preview.anchor_application_id,
    name: preview.anchor_name,
    type: "application",
  };
  const outcomes = preview.enrichment_review_outcomes || {};

  const queue = buildReviewQueue({
    preview,
    editingEntity: entity,
    relActions: {},
    catalogActions: {},
    matchActions: {},
    descriptionApplied: isDescriptionReviewResolved(preview),
  });
  return queue.length > 0;
}

/** Inverse of {@link previewHasOutstandingReview} for completed jobs. */
export function previewReviewComplete(preview, editingEntity = null) {
  if (!preview || typeof preview !== "object") return true;
  return !previewHasOutstandingReview(preview, editingEntity);
}

/** Completed enrichment job with at least one undecided AI suggestion. */
export function enrichmentJobNeedsReview(job) {
  if (String(job?.status || "").toLowerCase() !== "completed") return false;
  const preview = previewFromJob(job);
  if (!preview) return false;
  return previewHasOutstandingReview(preview);
}

/** Minimal anchor entity for review UI — avoids a separate GET /api/entities on open. */
export function anchorEntityFromEnrichmentPreview(preview) {
  if (!preview || typeof preview !== "object") return null;
  const id = String(preview.anchor_application_id || "").trim();
  if (!id) return null;
  const attrs =
    preview.anchor_attributes && typeof preview.anchor_attributes === "object"
      ? preview.anchor_attributes
      : {};
  const name =
    String(preview.anchor_name || attrs.name || attrs.entity_name || "").trim() ||
    "Application";
  const type =
    String(
      attrs.entity_type || attrs.type || preview.anchor_entity_type || "application"
    ).trim() || "application";
  return { id, name, type };
}

export function anchorSummaryLine(preview, editingEntity) {
  const name =
    String(editingEntity?.name || preview?.anchor_name || "").trim() || "Application";
  const method = String(preview?.anchor_resolution_method || "").toLowerCase();
  if (method === "auto_created" || preview?.anchor_is_new_provisional) {
    return `${name} · new app (we created it)`;
  }
  if (method === "llm") return `${name} · matched from your map (AI)`;
  if (method === "heuristic") return `${name} · name found in your upload`;
  if (method === "existing") return `${name} · exact match on your map`;
  if (method === "explicit") return `${name} · you picked this app`;
  return `${name}`;
}

/**
 * When enrichment suggested one entity type but catalog reconciliation picked another.
 * Uses resolution outcomes and optional proposal row hints from apply_resolution_type_alignments.
 */
export function catalogTypeAlignmentHint(preview, surfaceName, effectiveType, row = null) {
  const suggestedFromRow = String(row?.suggested_missing_entity_type || "").trim();
  const catalogType = String(effectiveType || row?.missing_entity_type || "").trim();
  if (suggestedFromRow && normName(suggestedFromRow) !== normName(catalogType)) {
    return {
      suggestedType: suggestedFromRow,
      catalogType,
      surfaceName: String(surfaceName || "").trim(),
    };
  }

  const surfaceNorm = normName(surfaceName);
  if (!surfaceNorm) return null;
  const typeNorm = normName(catalogType);
  const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];
  for (const res of rows) {
    if (normName(res?.surface_name) !== surfaceNorm) continue;
    const suggested = String(res?.suggested_entity_type || "").trim();
    const resolvedType = String(res?.entity_type || "").trim();
    if (!suggested || normName(suggested) === normName(resolvedType)) continue;
    if (
      res?.reason_code === "cross_type_catalog_match" ||
      normName(resolvedType) === typeNorm ||
      normName(suggested) === typeNorm
    ) {
      return {
        suggestedType: suggested,
        catalogType: resolvedType,
        surfaceName: res.surface_name,
        reasonCode: res.reason_code,
      };
    }
  }
  return null;
}

export function catalogTypeAlignmentMessage(hint) {
  if (!hint) return "";
  const catalog = formatCatalogEntityTypeLabel(hint.catalogType);
  const suggested = formatCatalogEntityTypeLabel(hint.suggestedType);
  return `Matched existing ${catalog} on your map (enrichment suggested ${suggested}).`;
}
